import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { RECYCLE_PRODUCT_DISPLAY_MS } from '@dooh/shared';
import {
  isOverlayVisible,
  playlistKeyForState,
  type RecycleContext,
  type RecycleOverlayState,
} from './recycle-machine';
import { resolveGenericQrUrl } from './resolve-recycle-media';
import productPlaceholder from './product-placeholder.webp';

export interface RecycleOverlayProps {
  ctx: RecycleContext;
  /** Resolve a reserved playlist key OR a CMS asset id to a playable URL, or null for built-in fallback. */
  resolveMedia: (keyOrAssetId: string) => string | null;
  /** Fire the ack_screen_done command (product dwell complete). */
  onScreenDone: (txnId: string | null) => void;
  /** Operator pressed "Item Cleared". */
  onOperatorClear: (txnId: string | null) => void;
  /** Product dwell expired → machine reset to idle. */
  onReset: () => void;
  /**
   * Whether the recycle agent's WS is currently connected. Defaults to `true`
   * (online) so plain-signage / test renders that never wire an agent don't
   * spuriously reset. When this drops to `false` mid-transaction the overlay
   * resets so a dead agent can't leave signage covered forever (#179).
   */
  agentOnline?: boolean;
}

/** States that auto-dwell then return to idle. `product` also acks.
 * `rejected` (v2) reuses this same dwell/dismiss pattern — no ack, since a
 * scan_rejected event never created a transaction. */
const AUTO_DWELL: RecycleOverlayState[] = [
  'product', 'no_match', 'barcode_not_qr', 'no_item_detected', 'rejected',
];

/**
 * In-flight states that suspend signage but have NO auto-dwell timer of their
 * own. If the agent crashes / its WS drops after the scan (or after the verdict
 * in deposit_item), no further event arrives — verdict_timeout is emitted BY
 * the agent, so a dead agent can't rescue the UI. A generous safety timeout
 * resets these back to idle so signage resumes (#179).
 */
export const SAFETY_TIMEOUT_STATES: RecycleOverlayState[] = ['processing', 'deposit_item'];

/**
 * In-flight states that must be released if the agent goes offline. Adds
 * `obstructed` (operator-recovery screen, which has no timeout) on top of the
 * safety-timeout states — a dead agent can never emit its recovery-success
 * event either (#179). `out_of_service` (v2) is included for the same
 * reason: it has no dwell timer, and if the WS drops entirely we can no
 * longer trust — or clear — a stale out-of-service reason.
 */
export const AGENT_OFFLINE_RESET_STATES: RecycleOverlayState[] = [
  'processing', 'deposit_item', 'obstructed', 'out_of_service',
];

/**
 * Generous upper bound on how long a non-auto-dwell in-flight screen
 * (processing / deposit_item) may stay up before we assume the agent died and
 * reset so signage resumes. Deliberately long so a merely-slow verdict isn't
 * cut short. Kept local (not in @dooh/shared) as a player-only recovery knob.
 */
export const RECYCLE_STUCK_TIMEOUT_MS = 40_000;

/** Screens whose media may be superseded by the verdict's playlistOverride. */
const OVERRIDE_STATES: RecycleOverlayState[] = ['deposit_item', 'product'];

// `out_of_service` and `rejected` render reason-specific copy (see
// OUT_OF_SERVICE_COPY / REJECTED_COPY below) instead of a static entry here.
const SCREEN_COPY: Record<Exclude<RecycleOverlayState, 'out_of_service' | 'rejected'>, { title: string; subtitle: string }> = {
  idle: { title: '', subtitle: '' },
  processing: { title: 'Reading…', subtitle: 'Checking your item' },
  deposit_item: { title: 'Deposit your item', subtitle: 'Place it in the bin' },
  product: { title: 'Thank you!', subtitle: 'Scan for your reward' },
  no_match: { title: 'Sorry, this item is not recyclable', subtitle: 'Please try another item' },
  barcode_not_qr: { title: 'Sorry, you scanned a QR code', subtitle: 'Please retry again' },
  no_item_detected: { title: 'No item detected', subtitle: 'Please try again' },
  obstructed: { title: 'Bin door blocked', subtitle: 'Please clear the item and press the button' },
  restart_required: { title: 'Out of service', subtitle: 'A technician has been notified' },
};

/**
 * v2: out_of_service copy varies by reason — bin_full gets a "service
 * scheduled" tone (routine, expected), hardware_fault a generic
 * "temporarily out of service" tone (unplanned fault). A missing/unknown
 * reason falls back to the hardware_fault copy (the more conservative,
 * less-specific claim).
 */
const OUT_OF_SERVICE_COPY: Record<'bin_full' | 'hardware_fault', { title: string; subtitle: string }> = {
  bin_full: { title: 'Bin full', subtitle: 'Service scheduled — thanks for recycling!' },
  hardware_fault: { title: 'Temporarily out of service', subtitle: 'A technician has been notified' },
};

/** v2: brief scan_rejected notice copy, same reason enum as out_of_service. */
const REJECTED_COPY: Record<'bin_full' | 'hardware_fault', { title: string; subtitle: string }> = {
  bin_full: { title: 'Bin full', subtitle: 'Sorry — please try again later' },
  hardware_fault: { title: "Can't scan right now", subtitle: 'Please try again in a moment' },
};

/**
 * Poster theme family per screen — drives accent/background CSS variables
 * (`rco-*` classes in index.css). `out_of_service`/`rejected` pick their
 * family by reason at render time: a full bin is routine (advisory/positive
 * tones), a hardware fault is a service tone.
 */
const SCREEN_FAMILY: Record<Exclude<RecycleOverlayState, 'out_of_service' | 'rejected'>, ThemeFamily> = {
  idle: 'active',
  processing: 'active',
  deposit_item: 'active',
  product: 'positive',
  no_match: 'advisory',
  barcode_not_qr: 'advisory',
  no_item_detected: 'advisory',
  obstructed: 'service',
  restart_required: 'service',
};

type ThemeFamily = 'positive' | 'active' | 'advisory' | 'service';
type IconName =
  | 'barcode' | 'drop' | 'check' | 'x-circle' | 'qr' | 'box' | 'alert' | 'wrench' | 'trash';

const SCREEN_ICON: Record<Exclude<RecycleOverlayState, 'out_of_service' | 'rejected'>, IconName> = {
  idle: 'barcode',
  processing: 'barcode',
  deposit_item: 'drop',
  product: 'check',
  no_match: 'x-circle',
  barcode_not_qr: 'qr',
  no_item_detected: 'box',
  obstructed: 'alert',
  restart_required: 'wrench',
};

/** Short poster eyebrow line per screen (kicker above the display title). */
const SCREEN_EYEBROW: Record<Exclude<RecycleOverlayState, 'out_of_service' | 'rejected'>, string> = {
  idle: '',
  processing: 'Scanning',
  deposit_item: 'Almost there',
  product: 'Reward unlocked',
  no_match: 'Heads up',
  barcode_not_qr: 'Heads up',
  no_item_detected: 'Heads up',
  obstructed: 'Action needed',
  restart_required: 'Be right back',
};

/** Inline stroke glyphs — tiny, currentColor, zero external assets. */
function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    barcode: (
      <>
        <path d="M3 5v14M7 5v14M11 5v9M11 17v2M15 5v14M19 5v9M19 17v2" />
      </>
    ),
    drop: (
      <>
        <path d="M12 3v11m0 0l-4.5-4.5M12 14l4.5-4.5" />
        <path d="M4 20h16" />
      </>
    ),
    check: <path d="M4.5 12.5l5 5L19.5 7" />,
    'x-circle': (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9 9l6 6M15 9l-6 6" />
      </>
    ),
    qr: (
      <>
        <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1" />
        <rect x="14" y="3.5" width="6.5" height="6.5" rx="1" />
        <rect x="3.5" y="14" width="6.5" height="6.5" rx="1" />
        <path d="M14 14h3v3h-3zM20.5 14v.01M17 20.5h-3M20.5 17.5v3" />
      </>
    ),
    box: (
      <>
        <path d="M4 8l8-4.5L20 8v8l-8 4.5L4 16z" />
        <path d="M4 8l8 4.5L20 8M12 12.5V20.5" />
      </>
    ),
    alert: (
      <>
        <path d="M12 3.5L22 20.5H2z" />
        <path d="M12 10v5M12 18.2v.01" />
      </>
    ),
    wrench: (
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    ),
    trash: (
      <>
        <path d="M3.5 6h17M8.5 6V4h7v2M5.5 6l1.2 14h10.6L18.5 6" />
        <path d="M10 10.5v6M14 10.5v6" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/**
 * v2: static fallback reward QR for accepts with no transaction-specific
 * `rewardQrUrl` (offline cached-catalog accepts, or a legacy agent build
 * that never sends one) AND no CMS-configured `recycle:generic-qr` reserved
 * playlist key (Task 5.2, see `resolveGenericQrUrl` in
 * resolve-recycle-media.ts) — a generic CC Rewards info destination so the
 * success screen ALWAYS renders a scannable QR.
 */
export const GENERIC_REWARD_QR_URL = 'https://crrewards.app';

/**
 * The thank-you (product) screen holds longer than the other transient
 * screens — time to admire the W, grab the phone, and scan the reward QR.
 * Rejection/notice screens keep the shorter shared dwell.
 */
export const RECYCLE_SUCCESS_DISPLAY_MS = 10_000;

/**
 * Campus-voiced success titles (Gen Z / Alpha energy) — one is picked per
 * successful recycle, keyed off the transaction id so the choice is stable
 * across re-renders of the same transaction but varies scan to scan.
 */
export const SUCCESS_TITLES = [
  'You Rock!',
  "Let's Go!",
  'Crushed It!',
  'Too Easy!',
  'Clutch!',
  'Legend!',
  'Iconic!',
  'Nice One!',
  'Big W!',
  "That's a W!",
  'Massive W!',
  'Certified W!',
  'W on the Campus!',
  'W for the Planet!',
  'W Energy!',
  'Dub Secured!',
  'Level Up!',
  'XP Earned!',
  'Locked In!',
  'You Built Different!',
  "You're the GOAT!",
  'You Did That!',
  'Another W!',
  "That's a DUB!",
  "You're AWESOME!",
  'Eco WIN!',
] as const;

/** Cheap stable string hash → success-title index. */
export function pickSuccessTitle(seed: string | null): string {
  let h = 0;
  const s = seed ?? '';
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return SUCCESS_TITLES[Math.abs(h) % SUCCESS_TITLES.length] ?? SUCCESS_TITLES[0];
}

/**
 * Fullscreen kiosk overlay for the recycle-kiosk experience. Renders nothing
 * when idle (signage plays untouched). Owns the auto-dwell timers for
 * terminal-but-transient screens (product/no_match/barcode_not_qr/
 * no_item_detected) — the reducer (Task 5) is pure and has no timers.
 *
 * The agent-offline badge is NOT rendered here (Task 10 / App.tsx owns it) —
 * per the brief, this component only draws interaction screens + recovery UI.
 */
export function RecycleOverlay(props: RecycleOverlayProps) {
  const { ctx, resolveMedia, onScreenDone, onOperatorClear, onReset, agentOnline = true } = props;
  const { state } = ctx;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaFailed, setMediaFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  // Auto-dwell timer for terminal-but-transient screens. The thank-you
  // screen holds 10s (reward-QR scan time); the notice screens keep the
  // shorter shared dwell.
  useEffect(() => {
    if (!AUTO_DWELL.includes(state)) return;
    const dwellMs = state === 'product' ? RECYCLE_SUCCESS_DISPLAY_MS : RECYCLE_PRODUCT_DISPLAY_MS;
    const timer = setTimeout(() => {
      if (state === 'product') onScreenDone(ctx.txnId);
      onReset();
    }, dwellMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, ctx.txnId]);

  // Safety timeout for non-auto-dwell in-flight screens (processing/
  // deposit_item). Without this, a recycle agent that crashes or whose WS drops
  // after the scan leaves the overlay stuck full-screen forever — playback
  // suspended, playlogs stopped — because the only event that could rescue it
  // (verdict_timeout) is emitted by the now-dead agent. The timer is keyed on
  // state + txnId, so any legitimate transition tears it down and restarts it
  // for the next in-flight screen (#179).
  useEffect(() => {
    if (!SAFETY_TIMEOUT_STATES.includes(state)) return;
    const timer = setTimeout(() => {
      onReset();
    }, RECYCLE_STUCK_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, ctx.txnId]);

  // Agent-offline reset: if the agent's WS drops while a transaction is
  // in-flight (processing/deposit_item/obstructed), nothing further can ever
  // arrive to release the overlay, so reset immediately rather than waiting out
  // the safety timeout (and, for obstructed, which has no timeout at all) (#179).
  useEffect(() => {
    if (agentOnline) return;
    if (!AGENT_OFFLINE_RESET_STATES.includes(state)) return;
    onReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOnline, state]);

  // Reset product image error flag whenever the product or state changes.
  useEffect(() => {
    setImageFailed(false);
  }, [ctx.state, ctx.product]);

  // Video autoplay and error handling.
  useEffect(() => {
    if (!videoRef.current) return;
    setMediaFailed(false); // Reset on media URL/state change
    const playPromise = videoRef.current.play();
    if (playPromise) {
      playPromise.catch(() => setMediaFailed(true));
    }
  }, [ctx.state, ctx.playlistOverride]);

  if (!isOverlayVisible(state)) return null;

  // Resolve media: verdict override asset id wins on deposit_item/product; else reserved key.
  const key = OVERRIDE_STATES.includes(state) && ctx.playlistOverride
    ? ctx.playlistOverride
    : playlistKeyForState(state);
  const mediaUrl = key ? resolveMedia(key) : null;
  // out_of_service / rejected copy varies by reason; every other screen uses
  // the static SCREEN_COPY map. A missing/unknown reason falls back to the
  // hardware_fault variant (the more conservative, less-specific claim).
  const copy =
    state === 'out_of_service' ? OUT_OF_SERVICE_COPY[ctx.outOfServiceReason ?? 'hardware_fault']
    : state === 'rejected' ? REJECTED_COPY[ctx.rejectedReason ?? 'hardware_fault']
    : SCREEN_COPY[state];

  // v2: the success screen always renders a scannable QR — the legacy
  // `reward` event's qrUrl (if it arrived) wins, then the verdict's
  // transaction-specific rewardQrUrl, then a CMS-configured generic QR
  // (`recycle:generic-qr` reserved key, Task 5.2), else the hardcoded
  // static fallback so there's never a blank success screen.
  const rewardQrValue =
    ctx.rewardUrl ?? ctx.rewardQrUrl ?? resolveGenericQrUrl(resolveMedia, GENERIC_REWARD_QR_URL);

  // Reason-aware theme/icon/eyebrow: bin_full reads as routine service
  // (advisory tones), hardware_fault as a service fault; every other screen
  // uses the static per-state maps.
  const family: ThemeFamily =
    state === 'out_of_service' ? ((ctx.outOfServiceReason ?? 'hardware_fault') === 'bin_full' ? 'advisory' : 'service')
    : state === 'rejected' ? 'advisory'
    : SCREEN_FAMILY[state];
  const icon: IconName =
    state === 'out_of_service' || state === 'rejected'
      ? ((state === 'out_of_service' ? ctx.outOfServiceReason : ctx.rejectedReason) ?? 'hardware_fault') === 'bin_full'
        ? 'trash'
        : 'wrench'
      : SCREEN_ICON[state];
  const eyebrow =
    state === 'out_of_service' ? 'Be right back'
    : state === 'rejected' ? 'Heads up'
    : SCREEN_EYEBROW[state];

  const showVideo = Boolean(mediaUrl) && !mediaFailed;
  const isDwell = AUTO_DWELL.includes(state);
  const dwellMs = state === 'product' ? RECYCLE_SUCCESS_DISPLAY_MS : RECYCLE_PRODUCT_DISPLAY_MS;
  // One campus-voiced hype title per successful recycle, stable per txn.
  const successTitle = pickSuccessTitle(ctx.txnId ?? ctx.barcode);

  return (
    <div
      data-testid={`recycle-screen-${state}`}
      // Key on state so the entrance stagger replays on every screen change.
      key={state}
      className={`rco rco-${family} absolute inset-0 z-40 overflow-hidden`}
    >
      <div className="rco-bg absolute inset-0" aria-hidden="true" />
      <div className="rco-watermark" aria-hidden="true">
        <Icon name={icon} />
      </div>

      {showVideo ? (
        <video
          ref={videoRef}
          data-testid="recycle-media"
          src={mediaUrl ?? undefined}
          className="absolute inset-0 h-full w-full object-contain"
          muted
          loop
          playsInline
          disableRemotePlayback
          onError={() => setMediaFailed(true)}
        />
      ) : null}
      {showVideo ? <div className="rco-vignette absolute inset-0" aria-hidden="true" /> : null}

      <div className={`relative z-10 flex h-full flex-col items-center justify-center px-10 text-center ${state === 'product' ? 'gap-4' : 'gap-5'}`}>
        {state === 'product' ? (
          <>
            {/* Duolingo-style celebration: the hype title springs in word by
                word (bouncy overshoot, transform/opacity only), ABOVE the
                product image. Keyed on txnId so it replays per recycle. */}
            <h1 className="rco-title rco-title-hype" key={ctx.txnId ?? successTitle}>
              {successTitle.split(' ').map((word, i, arr) => (
                <span
                  key={i}
                  className="rco-word"
                  style={{ animationDelay: `${0.05 + i * 0.12}s` }}
                >
                  {word}
                  {i < arr.length - 1 ? ' ' : ''}
                </span>
              ))}
            </h1>

            {/* Prominent product image (webp-first backend URLs; bundled webp
               illustration when the verdict carried no image or it failed to
               load — the slot ALWAYS renders). */}
            <div className="rco-in rco-in-1 rco-imgcard" aria-hidden="false">
              <img
                data-testid="recycle-product-image"
                src={!imageFailed && ctx.product?.imageUrl ? ctx.product.imageUrl : productPlaceholder}
                alt={ctx.product?.name ?? 'Recycled item'}
                className={
                  !imageFailed && ctx.product?.imageUrl
                    ? 'h-full w-auto rounded-2xl bg-white object-contain p-3'
                    : 'h-full w-auto rounded-2xl object-contain'
                }
                onError={() => setImageFailed(true)}
              />
              <span className="rco-badge">
                <span className="rco-ping" />
                <Icon name="check" />
              </span>
            </div>

            {/* Product identity directly under its image. */}
            {ctx.product && (
              <div
                data-testid="recycle-product-info"
                className="rco-in rco-in-2 rounded-full border border-white/15 bg-white/10 px-7 py-2.5 text-xl font-semibold"
              >
                {ctx.product.name}
                {ctx.product.brand ? (
                  <span className="font-normal opacity-70"> — {ctx.product.brand}</span>
                ) : null}
              </div>
            )}

            {/* The action: big CTA, then the QR with a nudging arrow so the
                next step is unmissable. */}
            <div className="rco-in rco-in-3 rco-cta">{copy.subtitle}</div>

            <div className="rco-in rco-in-4 rco-qrwrap">
              <svg
                className="rco-arrow"
                viewBox="0 0 48 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M2 12h38M32 4l8 8-8 8" />
              </svg>
              <div
                data-testid="recycle-reward-qr"
                data-qr-value={rewardQrValue}
                className="rco-pop flex flex-col items-center gap-3 rounded-3xl bg-white p-5"
              >
                <QRCodeSVG value={rewardQrValue} size={200} level="M" aria-label="Reward QR code" />
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-500">
                  Scan with your phone
                </div>
              </div>
            </div>

            {/* Confirmation line under the QR. */}
            {eyebrow ? <div className="rco-in rco-in-4 rco-eyebrow">{eyebrow}</div> : null}
          </>
        ) : (
          <>
            {eyebrow ? <div className="rco-in rco-eyebrow">{eyebrow}</div> : null}

            <div className="rco-in rco-in-1 rco-medallion" aria-hidden="true">
              {state === 'processing' && (
                <svg className="rco-orbit" viewBox="0 0 100 100" fill="none">
                  <circle
                    cx="50" cy="50" r="47" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeDasharray="10 14" opacity="0.7"
                  />
                </svg>
              )}
              <Icon name={icon} />
            </div>

            <h1 className="rco-in rco-in-2 rco-title">{copy.title}</h1>
            <p className="rco-in rco-in-3 rco-subtitle">{copy.subtitle}</p>
          </>
        )}

        {state === 'deposit_item' && (
          <div className="rco-in rco-in-4 mt-2 flex flex-col items-center" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <svg
                key={n}
                className={`rco-chevron ${n > 1 ? `rco-chevron-${n}` : ''} -my-1 h-9 w-9`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M5 9l7 7 7-7" />
              </svg>
            ))}
          </div>
        )}

        {state === 'obstructed' && (
          <button
            type="button"
            onClick={() => onOperatorClear(ctx.txnId)}
            className="rco-in rco-in-4 mt-4 rounded-full bg-white px-12 py-5 text-2xl font-semibold uppercase tracking-wider text-red-950 transition-transform duration-100 active:scale-95"
          >
            Item Cleared (attempt {ctx.obstructionAttempt})
          </button>
        )}
      </div>

      {isDwell && (
        <div className="rco-dwell" aria-hidden="true">
          <i style={{ animationDuration: `${dwellMs}ms` }} />
        </div>
      )}
    </div>
  );
}
